import { useDialog } from '@/common/dialog/DialogContext';
import { EntitySelectDialog } from '@/common/dialog/EntitySelectDialog';
import { Mode } from '@/common/entityFramework/enumerations/Mode';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { StringUtils } from '@/common/utils/StringUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Icon, IconButton, InputAdornment, TextField } from '@mui/material';
import { useMemo } from 'react';
import { useForm } from '../FormContext';
import { useFormFieldDef } from '../hooks/useFormFieldDef';

export const EntitySelect = ({ fieldDef, iconButton }) => {
  const { formValues, assignValue } = useForm();
  const { onBlur, error, helperText, label, disabled, variant, value } = useFormFieldDef(fieldDef);
  const { openDialog } = useDialog();
  const { navigateTo } = useNavState();
  const rsrc = useRsrc();

  const multiple = fieldDef.multiple;

  // Object used internally to track id, code, and name for display purposes for single selection
  const idCodeName = useMemo(() => {
    if (multiple) return null;

    // Special handling if this field is 'code' vs a FK Entity
    const isCode = fieldDef.field === 'code';

    const idField = isCode ? 'id' : fieldDef.field;
    if (!formValues[idField]) return null;

    if (isCode) {
      const { id, code, name } = formValues;
      return { id, code, name };
    }

    return {
      id: formValues[fieldDef.field],
      code: formValues[fieldDef.field.replace('Id', 'Code')],
      name: formValues[fieldDef.field.replace('Id', 'Name')],
    };
  }, [formValues]);

  // Handle click of the icon button
  const handleClick = (event) => {
    if (!multiple) {
      if (disabled) {
        // If disabled, this means the user navigates on click
        navigateToEntity(event.ctrlKey);
        return;
      }

      // If there is already a value, then clicking the button clears the current selection
      if (idCodeName) {
        assignFormValues({});
        return;
      }
    }

    // If there is no selection, open the EntitySelectDialog
    openEntitySelectDialog();
  };

  const navigateToEntity = async (openNewTab) => {
    let entity = fieldDef.entity;
    // PROD is abstract; resolve the concrete prod type so we navigate to the correct entity route
    if (entity === 'PROD') {
      const prod = await rsrc.byId(fieldDef.module, 'PROD', idCodeName.id);
      entity = prod.type + '_PROD';
    }
    const route = fieldDef.module + '/' + StringUtils.toKebabConvention(entity);
    const navState = {
      entityId: idCodeName.id,
      mode: Mode.DETAIL,
    };
    navigateTo(route, navState, openNewTab);
  };

  const openEntitySelectDialog = () => {
    openDialog(EntitySelectDialog, {
      data: {
        module: fieldDef.module,
        entity: fieldDef.entity,
        multiple: multiple,
        field: fieldDef.field,
        expr: fieldDef.expr,
        query: fieldDef.query,
        replaceParams: fieldDef.replaceParams,

        // For restoring state of multiple select
        headerChecked: formValues[fieldDef.field + 'HeaderChecked'],
        selectedIds: formValues[fieldDef.field + 'SelectedIds'],

        // For limiting the selection to active FKs of another entity (ex: only Custs that are a shipCust of DirSaleLines)
        parentFilterEntityModule: fieldDef.parentFilterEntityModule,
        parentFilterEntity: fieldDef.parentFilterEntity,
        parentFilterFkField: fieldDef.parentFilterFkField,
      },
    }).then((res) => {
      onBlur();

      // User canceled or clicked out of dialog
      if (res == null) {
        return;
      }

      assignFormValues(res);
    });
  };

  // Assign id, code, and name to the formValues
  const assignFormValues = (obj) => {
    if (!multiple) {
      const { selectedEntity } = obj;
      assignValue(fieldDef.field, selectedEntity?.id ?? null);
      assignValue(fieldDef.field.replace('Id', 'Code'), selectedEntity?.code ?? null);
      assignValue(fieldDef.field.replace('Id', 'Name'), selectedEntity?.name ?? null);
      return;
    }

    // In the multiple scenario, obj is an Expr
    const { expr, selectedCount, headerChecked, selectedIds } = obj;
    assignValue(fieldDef.field, expr); // The Expr actually used by the backend
    assignValue(fieldDef.field + 'SelectedCount', selectedCount); // Used for the label
    assignValue(fieldDef.field + 'HeaderChecked', headerChecked); // Used when reopening the dialog
    assignValue(fieldDef.field + 'SelectedIds', selectedIds); // Used when reopening the dialog
  };

  // Helper function to provide the text displayed in the input
  const inputText = () => {
    if (multiple) {
      return value == null ? 'All' : formValues[fieldDef.field + 'SelectedCount'] + ' Selected';
    }

    if (!idCodeName) {
      return '';
    }
    if (idCodeName.name) {
      return `${idCodeName.name} (${idCodeName.code})`;
    }
    return idCodeName.code;
  };

  // Helper function to provide the icon to the IconButton
  const icon = () => {
    if (multiple) {
      return fas.faSearch;
    }

    if (disabled) {
      return fas.faLink;
    }
    return idCodeName ? fas.faTimes : fas.faSearch;
  };

  const renderIconButton = () => {
    if (iconButton) return iconButton;

    // If disabled and no value, hide the button as we cannot navigate
    if (disabled && !idCodeName) return;

    return (
      <IconButton onClick={(event) => handleClick(event)} edge="end">
        <Icon>
          <FontAwesomeIcon style={{ width: '100%' }} icon={icon()} />
        </Icon>
      </IconButton>
    );
  };

  return (
    <TextField
      variant={variant}
      error={error}
      disabled={disabled}
      helperText={helperText}
      label={label}
      onBlur={onBlur}
      value={inputText()}
      slotProps={{
        input: {
          readOnly: true,
          endAdornment: <InputAdornment position="end">{renderIconButton()}</InputAdornment>,
        },
      }}
    />
  );
};
